import { LAYUP_SPOTS, MID_SPOTS, type SpotKey, THREE_SPOTS } from '../../core/spots';
import { COMBINE_LEVELS, CORE_COMBINE_LEVELS, type CombineLevel, type DrillShotClass } from '../../schemas/drill';

/**
 * The combine catalog (RULINGS R20, ADDENDUM A1). Pure data and a few helpers, used by the server
 * (the catalog route, attempt validation, the Iso rule) and the client (the recorder, results).
 *
 * Levels: The Arc (5 spots x 5 shots), Midrange Tour (5 x 5), Layup Line (5 left, 5 right),
 * Charity Stripe (10 free throws) and Iso Gauntlet (1v1 games to 3, no attempts). The first four
 * are the "core" combine. Their shot counts must equal CORE_LEVEL_SHOTS in schemas/drill.ts
 * (a test pins it).
 */

export interface PlanEntry {
  spot: SpotKey;
  shotClass: DrillShotClass;
  count: number;
}

export interface LevelInfo {
  level: CombineLevel;
  name: string;
  blurb: string;
  core: boolean;
  /** Shots per player. null for Iso Gauntlet. */
  shots: number | null;
  plan: PlanEntry[];
}

/** A turn at the line is at most this many shots in a row (one spot, one shooter). */
export const BLOCK_SIZE = 5;

/** Iso Gauntlet: games each player plays, and how many of them make the level "done". */
export const ISO_GAMES_EACH = 3;

const plan = (spots: readonly SpotKey[], shotClass: DrillShotClass, count: number): PlanEntry[] =>
  spots.map((spot) => ({ spot, shotClass, count }));

const LEVELS: Record<CombineLevel, Omit<LevelInfo, 'shots'>> = {
  arc: {
    level: 'arc',
    name: 'The Arc',
    blurb: 'Five spots behind the line, five shots from each.',
    core: true,
    plan: plan(THREE_SPOTS, 'three', 5),
  },
  midrange: {
    level: 'midrange',
    name: 'Midrange Tour',
    blurb: 'Baseline, elbow, free throw line, elbow, baseline. Five shots each.',
    core: true,
    plan: plan(MID_SPOTS, 'mid', 5),
  },
  layup: {
    level: 'layup',
    name: 'Layup Line',
    blurb: 'Five from the left side, five from the right. No dunking.',
    core: true,
    plan: plan(LAYUP_SPOTS, 'rim', 5),
  },
  charity: {
    level: 'charity',
    name: 'Charity Stripe',
    blurb: 'Ten free throws. The second set has the pressure on.',
    core: true,
    plan: [{ spot: 'ft', shotClass: 'ft', count: 10 }],
  },
  iso: {
    level: 'iso',
    name: 'Iso Gauntlet',
    blurb: 'Three 1v1 games to 3. Real games, real Elo.',
    core: false,
    plan: [],
  },
};

const sum = (p: readonly PlanEntry[]) => p.reduce((n, e) => n + e.count, 0);

/** Every level, in play order. */
export const COMBINE_CATALOG: readonly LevelInfo[] = COMBINE_LEVELS.map((l) => {
  const base = LEVELS[l];
  return { ...base, shots: base.plan.length ? sum(base.plan) : null };
});

export function levelInfo(level: CombineLevel): LevelInfo {
  return COMBINE_CATALOG.find((l) => l.level === level) as LevelInfo;
}

export function isCoreLevel(level: CombineLevel | null | undefined): level is (typeof CORE_COMBINE_LEVELS)[number] {
  return level !== null && level !== undefined && (CORE_COMBINE_LEVELS as readonly string[]).includes(level);
}

/** Shots one player takes in a level, or null for Iso Gauntlet. */
export function shotsPerPlayer(level: CombineLevel): number | null {
  return levelInfo(level).shots;
}

/** The shot class a level records. null for Iso Gauntlet (it records no shots). */
export function levelShotClass(level: CombineLevel): DrillShotClass | null {
  return levelInfo(level).plan[0]?.shotClass ?? null;
}

/** The spots a level uses, in order. */
export function levelSpots(level: CombineLevel): SpotKey[] {
  const seen: SpotKey[] = [];
  for (const e of levelInfo(level).plan) if (!seen.includes(e.spot)) seen.push(e.spot);
  return seen;
}

/** One stretch at one spot by one shooter: a spot's shots cut into runs of at most BLOCK_SIZE. */
export interface Block {
  index: number;
  spot: SpotKey;
  shotClass: DrillShotClass;
  count: number;
  /** The second set of free throws, "the pressure set". */
  pressure: boolean;
  /** Position among the blocks of the same spot (0 for the first run). */
  run: number;
}

/** The blocks of a level in order. Charity Stripe is two blocks of five at the line. */
export function levelBlocks(level: CombineLevel): Block[] {
  const out: Block[] = [];
  for (const e of levelInfo(level).plan) {
    let left = e.count;
    let run = 0;
    while (left > 0) {
      const count = Math.min(BLOCK_SIZE, left);
      out.push({
        index: out.length,
        spot: e.spot,
        shotClass: e.shotClass,
        count,
        pressure: level === 'charity' && run > 0,
        run,
      });
      left -= count;
      run += 1;
    }
  }
  return out;
}

/** Does a recorded shot make sense for the level? Used by the server to refuse junk. */
export function shotFitsLevel(level: CombineLevel, spot: SpotKey | null, shotClass: DrillShotClass): boolean {
  const info = levelInfo(level);
  if (info.plan.length === 0) return false;
  if (info.plan[0]?.shotClass !== shotClass) return false;
  return spot === null || info.plan.some((e) => e.spot === spot);
}
